import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Box, Container, Grid } from "@mui/material";
import axios from "axios";
import moment from "moment";

// Services, Web3 & Constants
import giftcardcinstance from "../../../Service/giftcardcinstance";
import web3 from "../../../Service/web3";
import NavBar from "../../../Components/NavBar";
import {
  Path,
  DEFAULT_CURRENCY,
  DEFAULT_TOKEN_SYMBOL,
} from "../../../../static/Constants";
import getImageUrlByHash from "../../../../static/logoUtils";
import AutobetLogo from "../../../../static/Images/logo.png";
import {
  getConnectedAccount,
  connectWallet as triggerConnectWallet,
  subscribeToWalletEvents,
} from "../../../../static/walletUtils";
import { colors } from "../../../../theme";
import { CommonBreadcrumbs, toast } from "../../../Components/ui";
import SuccessPopup from "../../../Components/SuccessPopup";
import FailedPopup from "../../../Components/FailedPopup";

// Subcomponents
import {
  GiftCardDetailHero,
  GiftCardDetailVisual,
  GiftCardDetailSpecs,
  GiftCardPurchaseCard,
  GiftCardDetailEmptyState,
  GiftCardDetailSkeleton,
} from "./components";

import "./BuyGiftCardStyle.css";

const withTimeout = (promise, ms = 6000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms)
    ),
  ]);
};

const safeFromWei = (val, unit = "ether") => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, unit);
  } catch (err) {
    try {
      const num = Number(val);
      if (isNaN(num)) return "0";
      return (num / 1e18).toFixed(4);
    } catch {
      return "0";
    }
  }
};

const BuyGiftCard = (props) => {
  const params = useParams();
  const location = useLocation();

  // Extract ID reliably from props.match or useParams, preserving 0
  const rawId =
    params?.id !== undefined && params?.id !== null && params?.id !== ""
      ? params.id
      : props?.match?.params?.id !== undefined
      ? props.match.params.id
      : "";

  const cardId = rawId !== "" ? String(rawId) : "";

  // Check localStorage for instant cache rendering
  const [giftcard, setGiftCard] = useState(() => {
    try {
      const saved = localStorage.getItem("all_giftcards");
      if (saved && cardId !== "") {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const match = parsed.find(
            (c) => String(c.giftCardId) === cardId || String(c.id) === cardId
          );
          if (match) return match;
        }
      }
    } catch {}
    return null;
  });

  const [giftcardLogoData, setGiftcardLogoData] = useState(() => {
    try {
      const saved = localStorage.getItem("giftcard_logos");
      if (saved) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch {}
    return [];
  });

  const [loading, setLoading] = useState(() => {
    try {
      const saved = localStorage.getItem("all_giftcards");
      if (saved && cardId !== "") {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const match = parsed.find(
            (c) => String(c.giftCardId) === cardId || String(c.id) === cardId
          );
          if (match) return false;
        }
      }
    } catch {}
    return true;
  });

  const [account, setAccount] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [isVoucherInvalid, setIsVoucherInvalid] = useState(false);
  const [voucherErrorMessage, setVoucherErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFail, setShowFail] = useState(false);

  const apiUrl = process.env.REACT_APP_API_KEY;

  // Pre-fill voucher code from URL query param
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const codeFromUrl = queryParams.get("voucherCode");
    if (codeFromUrl) {
      setVoucherCode(codeFromUrl);
    }
  }, [location.search]);

  // Track connected Web3 wallet
  useEffect(() => {
    let isMounted = true;
    getConnectedAccount().then((acc) => {
      if (isMounted && acc) setAccount(acc);
    });

    const unsubscribe = subscribeToWalletEvents({
      onAccountsChanged: (newAcc) => {
        if (isMounted) setAccount(newAcc || "");
      },
      onChainChanged: () => {
        if (isMounted) window.location.reload();
      },
      onDisconnect: () => {
        if (isMounted) setAccount("");
      },
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  /**
   * Fetch On-Chain Gift Card Data with 7s Watchdog
   */
  const fetchCardData = useCallback(async () => {
    if (cardId === "") {
      setLoading(false);
      return;
    }

    // Hard watchdog timer so page NEVER gets stuck on loading spinner
    const watchdog = setTimeout(() => {
      setLoading(false);
    }, 7000);

    try {
      // 1. Fetch logos in background
      axios
        .get(`${apiUrl}/giftcardlogos/getAllGiftcardLogos`, { timeout: 5000 })
        .then((response) => {
          if (response.data && response.data.result) {
            setGiftcardLogoData(response.data.result);
            try {
              localStorage.setItem(
                "giftcard_logos",
                JSON.stringify(response.data.result)
              );
            } catch {}
          }
        })
        .catch((err) => console.warn("[BuyGiftCard] Logo fetch error:", err));

      // 2. Query smart contract for card data
      const response = await withTimeout(
        giftcardcinstance.methods.giftCards(cardId).call(),
        5500
      );

      // Verify creator address exists (non-zero address)
      if (
        response &&
        response.creator &&
        response.creator !== "0x0000000000000000000000000000000000000000"
      ) {
        const enriched = {
          ...response,
          giftCardId:
            response.giftCardId !== undefined && response.giftCardId !== ""
              ? response.giftCardId
              : cardId,
        };
        setGiftCard(enriched);
      } else {
        setGiftCard(null);
      }
    } catch (err) {
      console.error("[BuyGiftCard] Fetch error:", err);
    } finally {
      clearTimeout(watchdog);
      setLoading(false);
    }
  }, [cardId, apiUrl]);

  useEffect(() => {
    fetchCardData();
  }, [fetchCardData]);

  // Connect Wallet Handler
  const handleConnectWallet = async () => {
    try {
      const res = await triggerConnectWallet();
      if (res && res.account) {
        setAccount(res.account);
        toast.success("Wallet connected successfully!");
      }
    } catch (err) {
      console.error("Wallet connection error:", err);
      toast.error("Failed to connect wallet.");
    }
  };

  // Share Card Handler
  const handleShare = () => {
    let shareUrl = `${window?.location?.origin}/giftcard/${cardId}`;
    if (giftcard?.isDonation && giftcard?.voucherCode) {
      shareUrl += `?voucherCode=${giftcard.voucherCode}`;
    }
    try {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Gift card link copied to clipboard!");
    } catch (err) {
      console.error("Copy error:", err);
    }
  };

  // Redeem / Purchase Submission Handler
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!giftcard) return;

    const isDonation =
      giftcard.isDonation === true ||
      giftcard.isDonation === "true" ||
      giftcard.isDonation === 1;

    // Validation for donation vouchers
    if (isDonation) {
      if (!voucherCode || voucherCode.trim() === "") {
        setIsVoucherInvalid(true);
        setVoucherErrorMessage("Please enter the secret voucher code.");
        return;
      }

      if (
        giftcard.voucherCode &&
        voucherCode.trim() !== giftcard.voucherCode.trim()
      ) {
        setIsVoucherInvalid(true);
        setVoucherErrorMessage("Invalid voucher code. Please check and try again.");
        return;
      }
    }

    setIsVoucherInvalid(false);
    setVoucherErrorMessage("");

    // Check Connected Wallet
    let activeAccount = account;
    if (!activeAccount) {
      activeAccount = await getConnectedAccount();
      if (!activeAccount) {
        const connectRes = await triggerConnectWallet();
        activeAccount = connectRes?.account;
      }
    }

    if (!activeAccount) {
      toast.warn("Please connect your wallet to proceed.");
      return;
    }

    setIsSubmitting(true);

    try {
      const valueToSend = isDonation ? "0" : giftcard.ETHPrice || "0";

      await giftcardcinstance.methods
        .purchaseGiftCard(cardId, isDonation ? voucherCode.trim() : "")
        .send({
          from: activeAccount,
          value: valueToSend,
        });

      setShowSuccess(true);
      fetchCardData();
    } catch (err) {
      console.error("[BuyGiftCard] Transaction error:", err);
      setShowFail(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Process Card Attributes
  const isDonation = Boolean(
    giftcard?.isDonation === true ||
      giftcard?.isDonation === "true" ||
      giftcard?.isDonation === 1
  );
  const isSold = Boolean(
    giftcard?.isSold === true ||
      giftcard?.isSold === "true" ||
      giftcard?.isSold === 1
  );
  const expTimestamp = Number(giftcard?.expDate || 0);
  const isExpired = expTimestamp > 0 && expTimestamp <= moment().unix();

  const tokenValue = giftcard ? safeFromWei(giftcard.BETValue, "ether") : "0";
  const tokenName = giftcard?.tokenName || DEFAULT_TOKEN_SYMBOL;
  const price = giftcard ? safeFromWei(giftcard.ETHPrice, "ether") : "0";
  const imageUrl = giftcard
    ? getImageUrlByHash(giftcard.logoHash, giftcardLogoData) || AutobetLogo
    : AutobetLogo;

  return (
    <Box className="giftcard-canvas" sx={{ bgcolor: colors.dashboard.canvas, minHeight: "100vh", pb: 8 }}>
      <NavBar />

      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: "96px", sm: "108px", md: "116px" },
          pb: 6,
          flexGrow: 1,
        }}
      >
        {/* Breadcrumb Navigation */}
        <CommonBreadcrumbs
          items={[
            { label: "Home", path: Path.root },
            { label: "All Gift Cards", path: Path.allgiftcards },
            {
              label:
                cardId !== ""
                  ? `Card #${cardId}`
                  : isDonation
                  ? "Redeem Gift Card"
                  : "Buy Gift Card",
            },
          ]}
        />

        {/* Content State Handling */}
        {loading ? (
          <GiftCardDetailSkeleton />
        ) : giftcard ? (
          <>
            {/* Header / Hero Banner */}
            <GiftCardDetailHero
              cardId={cardId}
              isDonation={isDonation}
              tokenValue={tokenValue}
              tokenName={tokenName}
              isSold={isSold}
              expDate={expTimestamp}
              onShare={handleShare}
            />

            {/* Main Details & Checkout Grid */}
            <Grid container spacing={3.5} alignItems="flex-start">
              {/* Left Column: Visual, Description, Specifications & How-To */}
              <Grid item xs={12} md={7} lg={8}>
                <GiftCardDetailVisual
                  imageUrl={imageUrl}
                  cardId={cardId}
                  isDonation={isDonation}
                  tokenValue={tokenValue}
                  tokenName={tokenName}
                  description={giftcard.Desc}
                />

                <GiftCardDetailSpecs
                  creator={giftcard.creator}
                  tokenName={tokenName}
                  tokenValue={tokenValue}
                  price={price}
                  isDonation={isDonation}
                  currency={DEFAULT_CURRENCY}
                  expDate={expTimestamp}
                />
              </Grid>

              {/* Right Column: Checkout & Claim Action Box */}
              <Grid item xs={12} md={5} lg={4}>
                <GiftCardPurchaseCard
                  isDonation={isDonation}
                  tokenValue={tokenValue}
                  tokenName={tokenName}
                  price={price}
                  currency={DEFAULT_CURRENCY}
                  voucherCode={voucherCode}
                  onVoucherCodeChange={(val) => {
                    setVoucherCode(val);
                    if (isVoucherInvalid) {
                      setIsVoucherInvalid(false);
                      setVoucherErrorMessage("");
                    }
                  }}
                  isVoucherInvalid={isVoucherInvalid}
                  voucherErrorMessage={voucherErrorMessage}
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                  isSold={isSold}
                  isExpired={isExpired}
                  account={account}
                  onConnectWallet={handleConnectWallet}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <GiftCardDetailEmptyState cardId={cardId} onRetry={fetchCardData} />
        )}
      </Container>

      {/* Transaction Result Modals */}
      <SuccessPopup
        show={showSuccess}
        setShowModal={(e) => setShowSuccess(e)}
        message={
          isDonation
            ? "Gift voucher redeemed successfully! Tokens transferred to your wallet."
            : "Gift card purchased successfully! Tokens transferred to your wallet."
        }
        redirectPath={Path.profile}
        btnName="Go to profile"
      />

      <FailedPopup
        show={showFail}
        setShowModal={(e) => setShowFail(e)}
      />
    </Box>
  );
};

export default BuyGiftCard;
